// pages/ShareLinks.jsx — Halaman admin untuk membuat & mengelola public share link.
// Akses: admin saja (digate di sidebar/route via permission sentinel '__admin__',
// dan backend /api/share juga requireAdmin).
import React, { useState, useEffect, useCallback } from 'react';
import {
  Link2, Plus, Copy, Check, Trash2, Loader2, ExternalLink, ShieldAlert, RefreshCw,
} from 'lucide-react';
import { useLang } from '../context/LanguageContext';

const API = (typeof window !== 'undefined' && window.__AWS_API_BASE__) || '';

const EXPIRY_OPTIONS = [
  { value: '7',  labelId: '7 hari',   labelEn: '7 days'   },
  { value: '30', labelId: '30 hari',  labelEn: '30 days'  },
  { value: '90', labelId: '90 hari',  labelEn: '90 days'  },
  { value: '',   labelId: 'Permanen', labelEn: 'Permanent'},
];

function authHeaders(extra = {}) {
  const token = localStorage.getItem('auth_token');
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...extra };
}

async function api(path, opts = {}) {
  const res  = await fetch(API + path, { ...opts, headers: authHeaders(opts.headers) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

const STATUS_STYLE = {
  active:  'bg-emerald-50 text-emerald-700',
  expired: 'bg-amber-50 text-amber-700',
  revoked: 'bg-slate-100 text-slate-500',
};

export default function ShareLinks() {
  const { lang } = useLang();
  const id = lang !== 'en';
  const T = (idStr, enStr) => (id ? idStr : enStr);

  const [links,    setLinks]    = useState([]);
  const [catalog,  setCatalog]  = useState([]); // auto-detect dari backend
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  const [dashKey,  setDashKey]  = useState('');
  const [expiry,   setExpiry]   = useState('7');
  const [aiEnabled, setAiEnabled] = useState(false);
  const [creating, setCreating] = useState(false);
  const [copied,   setCopied]   = useState(null); // token yang baru disalin

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [linkData, cat] = await Promise.all([
        api('/api/share'),
        api('/api/share/catalog'),
      ]);
      setLinks(linkData);
      setCatalog(Array.isArray(cat) ? cat : []);
      // Set default pilihan ke item pertama bila belum dipilih / tidak ada di catalog
      setDashKey(prev => {
        const shareableKeys = (Array.isArray(cat) ? cat : []).filter(d => d.shareable).map(d => d.key);
        // default ke item shareable pertama
        if (shareableKeys.includes(prev)) return prev;
        return shareableKeys[0] || '';
      });
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const shareableCatalog = catalog.filter(d => d.shareable);
  const selectedDash = catalog.find(d => d.key === dashKey);

  const handleCreate = async () => {
    if (!dashKey || !selectedDash?.shareable) return;
    setCreating(true); setError(null);
    try {
      const body = { dashboardKey: dashKey };
      if (expiry) body.expiresInDays = Number(expiry);
      if (aiEnabled && selectedDash?.aiAvailable) body.aiEnabled = true;
      await api('/api/share', { method: 'POST', body: JSON.stringify(body) });
      await load();
    } catch (e) { setError(e.message); }
    finally { setCreating(false); }
  };

  const toggleAi = async (token, next) => {
    try { await api(`/api/share/${token}`, { method: 'PATCH', body: JSON.stringify({ aiEnabled: next }) }); await load(); }
    catch (e) { setError(e.message); }
  };

  const handleRevoke = async (token) => {
    if (!window.confirm(T('Cabut link ini? Penerima tidak bisa mengaksesnya lagi.',
                          'Revoke this link? Recipients will lose access.'))) return;
    try { await api(`/api/share/${token}`, { method: 'DELETE' }); await load(); }
    catch (e) { setError(e.message); }
  };

  const copy = async (url, token) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(token);
      setTimeout(() => setCopied(c => (c === token ? null : c)), 1800);
    } catch { /* clipboard bisa diblokir; abaikan */ }
  };

  const fmt = (s) => (s ? new Date(s).toLocaleString(id ? 'id-ID' : 'en-US',
    { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

  return (
    <div className="p-6 max-w-5xl mx-auto content-animate">
      {/* Heading */}
      <div className="flex items-center gap-3 mb-1">
        <div className="w-9 h-9 rounded-xl bg-teal-50 flex items-center justify-center">
          <Link2 className="text-teal-500" style={{ width: 18, height: 18 }} />
        </div>
        <h1 className="font-display text-xl font-bold text-slate-800">Share Links</h1>
      </div>
      <p className="text-sm text-slate-500 mb-6">
        {T('Buat link publik hanya-baca untuk dibagikan tanpa username & password.',
           'Create public read-only links to share without a username & password.')}
      </p>

      {error && (
        <div className="mb-5 px-4 py-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2.5">
          <ShieldAlert className="text-red-500 flex-shrink-0 mt-0.5" style={{ width: 16, height: 16 }} />
          <p className="text-sm text-red-700 font-medium">{error}</p>
        </div>
      )}

      {/* Form buat link */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5 mb-6 shadow-sm">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-semibold text-slate-500 mb-1.5">{T('Dashboard', 'Dashboard')}</label>
            <select
              value={dashKey}
              onChange={e => setDashKey(e.target.value)}
              disabled={shareableCatalog.length === 0}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm font-medium text-slate-700 outline-none focus:border-blue-400 focus:bg-white disabled:opacity-50"
            >
              {shareableCatalog.length === 0 && (
                <option value="">{T('Tidak ada dashboard tersedia', 'No dashboards available')}</option>
              )}
              {shareableCatalog.map(d => (
                <option key={d.key} value={d.key}>{d.label}</option>
              ))}
            </select>
          </div>

          <div className="min-w-[140px]">
            <label className="block text-xs font-semibold text-slate-500 mb-1.5">{T('Masa berlaku', 'Expiry')}</label>
            <select
              value={expiry}
              onChange={e => setExpiry(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm font-medium text-slate-700 outline-none focus:border-blue-400 focus:bg-white"
            >
              {EXPIRY_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{id ? o.labelId : o.labelEn}</option>
              ))}
            </select>
          </div>

          <button
            onClick={handleCreate}
            disabled={creating || !dashKey || !selectedDash?.shareable}
            className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white text-sm font-semibold rounded-xl transition-all"
          >
            {creating
              ? <Loader2 className="animate-spin" style={{ width: 15, height: 15 }} />
              : <Plus style={{ width: 15, height: 15 }} />}
            {T('Buat Link', 'Create Link')}
          </button>
        </div>

        {selectedDash?.aiAvailable && (
          <label className="flex items-center gap-2 mt-3 text-sm text-slate-600 cursor-pointer select-none">
            <input type="checkbox" checked={aiEnabled} onChange={e => setAiEnabled(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-400" />
            {T('Aktifkan AI chat pada link ini (internal — penerima bisa tanya data dashboard ini)',
               'Enable AI chat on this link (internal — recipients can ask about this dashboard’s data)')}
          </label>
        )}

      </div>

      {/* Daftar link */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-slate-600">{T('Link Aktif & Riwayat', 'Active Links & History')}</h2>
        <button onClick={load} className="flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-slate-600">
          <RefreshCw style={{ width: 13, height: 13 }} /> {T('Muat ulang', 'Refresh')}
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-slate-400">
          <Loader2 className="animate-spin" style={{ width: 22, height: 22 }} />
        </div>
      ) : links.length === 0 ? (
        <div className="text-center py-12 text-sm text-slate-400 bg-white rounded-2xl border border-dashed border-slate-200">
          {T('Belum ada link. Buat link pertama di atas.', 'No links yet. Create your first link above.')}
        </div>
      ) : (
        <div className="space-y-3">
          {links.map(l => {
            const url = l.url.replace(/^http:\/\//i, 'https://');
            return (
              <div key={l.token} className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="font-semibold text-sm text-slate-800">{l.label}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wide ${STATUS_STYLE[l.status] || STATUS_STYLE.revoked}`}>
                    {l.status === 'active' ? T('aktif', 'active') : l.status === 'expired' ? T('kedaluwarsa', 'expired') : T('dicabut', 'revoked')}
                  </span>
                  <span className="ml-auto text-xs text-slate-400">{T('akses', 'hits')}: {l.hits ?? 0}</span>
                  {l.aiAvailable && (
                    <button
                      onClick={() => toggleAi(l.token, !l.aiEnabled)}
                      disabled={l.status === 'revoked'}
                      title={T('Aktifkan/nonaktifkan AI chat', 'Toggle AI chat')}
                      className={`px-2 py-0.5 rounded-full text-xs font-semibold border transition-colors disabled:opacity-40 ${
                        l.aiEnabled
                          ? 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
                          : 'bg-slate-50 text-slate-400 border-slate-200 hover:bg-slate-100'}`}
                    >
                      {l.aiEnabled ? T('✨ AI: aktif', '✨ AI: on') : T('AI: mati', 'AI: off')}
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-2 mb-2">
                  <input
                    readOnly
                    value={url}
                    onFocus={e => e.target.select()}
                    className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-xs font-mono text-slate-600 outline-none"
                  />
                  <button
                    onClick={() => copy(url, l.token)}
                    title={T('Salin', 'Copy')}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50"
                  >
                    {copied === l.token
                      ? <><Check className="text-emerald-500" style={{ width: 14, height: 14 }} /> {T('Tersalin', 'Copied')}</>
                      : <><Copy style={{ width: 14, height: 14 }} /> {T('Salin', 'Copy')}</>}
                  </button>
                  <a
                    href={url} target="_blank" rel="noreferrer"
                    title={T('Buka', 'Open')}
                    className="flex items-center px-3 py-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"
                  >
                    <ExternalLink style={{ width: 14, height: 14 }} />
                  </a>
                  <button
                    onClick={() => handleRevoke(l.token)}
                    disabled={l.status === 'revoked'}
                    title={T('Cabut', 'Revoke')}
                    className="flex items-center px-3 py-2 rounded-lg border border-red-200 text-red-500 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Trash2 style={{ width: 14, height: 14 }} />
                  </button>
                </div>

                <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-400">
                  <span>{T('Kedaluwarsa', 'Expires')}: {l.expiresAt ? fmt(l.expiresAt) : T('Permanen', 'Permanent')}</span>
                  <span>{T('Dibuat', 'Created')}: {fmt(l.createdAt)}{l.createdBy ? ` · ${l.createdBy}` : ''}</span>
                  <span>{T('Akses terakhir', 'Last access')}: {fmt(l.lastAccess)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
